import { FLEX_DEMO_STEPS, flexDemoSessionStorageKey, type FlexDemoStep } from "./flexDemo.const";

/**
 * Workspace tab tour. Each tab is shown on its own, then the coach points at the next tab.
 * `finish` reveals Finish walkthrough after Workspace has been shown.
 */
export type FlexDemoWorkspaceTab =
  | "overview"
  | "to-reports"
  | "reports"
  | "to-workspace"
  | "workspace"
  | "finish";

export type FlexDemoSession = {
  step: FlexDemoStep;
  ndaAccepted: boolean;
  selectedProposalId: string | null;
  answers: Record<string, string>;
  welcomeDismissed: boolean;
  collaborationsNavOpened: boolean;
  workspaceOpened: boolean;
  /** Which collaboration tab the walkthrough is demonstrating. */
  workspaceTab: FlexDemoWorkspaceTab;
  /** True after the signup user chooses to start the walkthrough. */
  accepted: boolean;
  /** Started from the header Demo button, including after the signup demo was skipped or finished. */
  replay: boolean;
};

const DEFAULT_SESSION: FlexDemoSession = {
  step: "explore",
  ndaAccepted: false,
  selectedProposalId: null,
  answers: {},
  welcomeDismissed: false,
  collaborationsNavOpened: false,
  workspaceOpened: false,
  workspaceTab: "overview",
  accepted: false,
  replay: false,
};

export function createFlexDemoSession(overrides: Partial<FlexDemoSession> = {}): FlexDemoSession {
  return { ...DEFAULT_SESSION, ...overrides };
}

function isStep(value: unknown): value is FlexDemoStep {
  return typeof value === "string" && (FLEX_DEMO_STEPS as readonly string[]).includes(value);
}

function isWorkspaceTab(value: unknown): value is FlexDemoWorkspaceTab {
  return (
    value === "overview" ||
    value === "to-reports" ||
    value === "reports" ||
    value === "to-workspace" ||
    value === "workspace" ||
    value === "finish"
  );
}

export function loadFlexDemoSession(userId: string): FlexDemoSession {
  try {
    const raw = sessionStorage.getItem(flexDemoSessionStorageKey(userId));
    if (!raw) return { ...DEFAULT_SESSION };
    const parsed = JSON.parse(raw) as Partial<FlexDemoSession>;
    return {
      step: isStep(parsed.step) ? parsed.step : "explore",
      ndaAccepted: Boolean(parsed.ndaAccepted),
      selectedProposalId: typeof parsed.selectedProposalId === "string" ? parsed.selectedProposalId : null,
      answers: parsed.answers && typeof parsed.answers === "object" ? parsed.answers : {},
      welcomeDismissed: Boolean(parsed.welcomeDismissed),
      collaborationsNavOpened: Boolean(parsed.collaborationsNavOpened),
      workspaceOpened: Boolean(parsed.workspaceOpened),
      workspaceTab: isWorkspaceTab(parsed.workspaceTab) ? parsed.workspaceTab : "overview",
      accepted: Boolean(parsed.accepted),
      replay: Boolean(parsed.replay),
    };
  } catch {
    return { ...DEFAULT_SESSION };
  }
}

export function saveFlexDemoSession(userId: string, session: FlexDemoSession): void {
  try {
    sessionStorage.setItem(flexDemoSessionStorageKey(userId), JSON.stringify(session));
  } catch {
    /* ignore quota / private mode */
  }
}

export function isFlexDemoReplaySession(userId: string | null | undefined): boolean {
  if (!userId) return false;
  return loadFlexDemoSession(userId).replay;
}

export function clearFlexDemoSession(userId: string): void {
  try {
    sessionStorage.removeItem(flexDemoSessionStorageKey(userId));
  } catch {
    /* ignore */
  }
}

export function nextFlexDemoStep(step: FlexDemoStep): FlexDemoStep {
  const index = FLEX_DEMO_STEPS.indexOf(step);
  return FLEX_DEMO_STEPS[Math.min(index + 1, FLEX_DEMO_STEPS.length - 1)];
}

export function prevFlexDemoStep(step: FlexDemoStep): FlexDemoStep {
  const index = FLEX_DEMO_STEPS.indexOf(step);
  return FLEX_DEMO_STEPS[Math.max(index - 1, 0)];
}
